import { useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Upload01, XCircle } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { knowledgeBaseService, knowledgeDocumentService } from '@/data/mock-knowledge-bases';
import type { KnowledgeBaseType } from '@/types/knowledge-base';
import { useTranslation } from 'react-i18next';

export const KnowledgeBaseNewPage = () => {
    const { t } = useTranslation(['pages:knowledge-admin', 'common']);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        kb_type: '' as KnowledgeBaseType | '',
    });
    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const typeOptions = [
        { id: 'general', label: t('types.general') },
        { id: 'documents', label: t('types.documents') },
        { id: 'routes', label: t('types.routes') },
        { id: 'regulation', label: t('types.regulation') },
        { id: 'company/account-specific', label: t('types.companyAccountSpecific') },
    ];

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        setUploadFiles([...uploadFiles, ...files]);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            setUploadFiles([...uploadFiles, ...files]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setUploadFiles(uploadFiles.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!formData.name.trim() || !formData.kb_type) {
            return;
        }

        setIsSaving(true);

        try {
            // Create knowledge base
            const newKB = knowledgeBaseService.create({
                name: formData.name.trim(),
                description: formData.description.trim() || undefined,
                kb_type: formData.kb_type as KnowledgeBaseType,
            });

            // Upload documents if any
            if (uploadFiles.length > 0) {
                for (const file of uploadFiles) {
                    await knowledgeDocumentService.upload(newKB.knowledge_base_id, file);
                }
            }

            navigate(`/admin/knowledge/${newKB.knowledge_base_id}`);
        } catch (error) {
            console.error('Error creating knowledge base:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{t('form.new')}</h2>
                    <p className="text-tertiary mt-1">{t('subtitle')}</p>
                </div>
                <Button
                    onClick={() => navigate('/admin/knowledge')}
                    size="md"
                    color="secondary"
                    iconLeading={ArrowLeft}
                >
                    {t('form.cancel')}
                </Button>
            </div>

            {/* Form */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                    <Input
                        label={t('form.fields.name')}
                        placeholder={t('form.fields.namePlaceholder')}
                        value={formData.name}
                        onChange={(value) => setFormData({ ...formData, name: value })}
                        isRequired
                        size="md"
                    />

                    <Input
                        label={t('form.fields.description')}
                        placeholder={t('form.fields.descriptionPlaceholder')}
                        value={formData.description}
                        onChange={(value) => setFormData({ ...formData, description: value })}
                        size="md"
                    />

                    <Select
                        label={t('form.fields.type')}
                        placeholder={t('form.fields.typePlaceholder')}
                        items={typeOptions}
                        selectedKey={formData.kb_type}
                        onSelectionChange={(key) => setFormData({ ...formData, kb_type: key as KnowledgeBaseType | '' })}
                        isRequired
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>

                {/* Documents Upload */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-primary">{t('form.documents.title')}</h3>

                    <div
                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging ? 'border-brand bg-brand/10' : 'border-secondary'
                            }`}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <Upload01 className="size-12 mx-auto mb-4 text-tertiary" />
                        <p className="text-primary mb-2">{t('form.documents.uploadArea')}</p>
                        <p className="text-sm text-tertiary mb-4">{t('form.documents.supportedFormats')}</p>
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            size="md"
                            color="secondary"
                        >
                            {t('form.documents.selectFile')}
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.docx,.txt,.md,.json"
                            onChange={handleFileSelect}
                            className="hidden"
                        />
                    </div>

                    {/* Selected Files */}
                    {uploadFiles.length > 0 && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-primary">{t('form.documents.selectedFiles')}</label>
                            <div className="space-y-1">
                                {uploadFiles.map((file, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between rounded-lg bg-secondary p-2"
                                    >
                                        <span className="text-sm text-primary">{file.name}</span>
                                        <button
                                            onClick={() => handleRemoveFile(index)}
                                            className="text-error hover:text-error_hover"
                                        >
                                            <XCircle className="size-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-4 pt-4 border-t border-secondary">
                    <Button
                        onClick={() => navigate('/admin/knowledge')}
                        size="md"
                        color="secondary"
                    >
                        {t('form.cancel')}
                    </Button>
                    <Button
                        onClick={handleSave}
                        size="md"
                        color="primary"
                        disabled={!formData.name.trim() || !formData.kb_type || isSaving}
                    >
                        {isSaving ? t('loading') : t('form.create')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
